import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";

/**
 * Generic dialog shown when the backend returns a 409 duplicate.
 * Three actions per session:
 *   - "Utiliser celui-ci" on a match → onUseExisting(match) (only if onUseExisting is provided)
 *   - "Créer un nouveau" → onConfirm() (force=true)
 *   - "Annuler" → close
 */
const DuplicateConfirmDialog = ({ open, onOpenChange, info, onConfirm, onUseExisting }) => {
  if (!info) return null;
  const matches = info.matches || [];
  const labelByEntity = {
    contact: "contact",
    company: "société",
    opportunity: "opportunité",
    quote: "devis",
  };
  const label = labelByEntity[info.entity] || "élément";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Doublon détecté dans Sellsy
          </DialogTitle>
          <DialogDescription>{info.message}</DialogDescription>
        </DialogHeader>

        {matches.length > 0 && (
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-60 overflow-y-auto">
            {matches.map((m, i) => {
              const amountStr = typeof m.amount === "object" && m.amount !== null
                ? `${m.amount.value ?? ""} ${m.amount.currency ?? ""}`.trim()
                : m.amount;
              return (
                <div key={m.sellsy_id || i} className="px-3 py-2 text-sm flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 truncate">
                      {m.name || m.subject || m.number || `#${m.sellsy_id}`}
                    </div>
                    {m.email && <div className="text-xs text-slate-500 truncate">{m.email}</div>}
                    {amountStr && <div className="text-xs text-slate-500">{amountStr}</div>}
                    <div className="text-[10px] text-slate-400">Sellsy #{m.sellsy_id}</div>
                  </div>
                  {onUseExisting && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 text-xs h-7"
                      onClick={() => {
                        // IMPORTANT: resolve the promise BEFORE closing the dialog,
                        // otherwise onOpenChange(false) auto-cancels with `undefined`
                        // first and our useExisting payload is lost (race condition).
                        onUseExisting(m);
                        onOpenChange(false);
                      }}
                      data-testid={`duplicate-use-existing-${i}`}
                    >
                      Utiliser celui-ci
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="duplicate-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={() => {
              // Same race fix: invoke onConfirm FIRST so it can synchronously
              // claim the resolver ref before onOpenChange(false) auto-cancels.
              onConfirm();
              onOpenChange(false);
            }}
            data-testid="duplicate-confirm-btn"
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            Créer un nouveau {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateConfirmDialog;
